import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../config/api';
import { claimUserScopedStorage, clearSessionStorageKeys } from '../utils/userScopedStorage';
import jwtDecode from 'jwt-decode';

const AuthContext = createContext(null);

// After a login (password, Google, Microsoft, MFA) the user is the token payload, which has `is_company_admin`;
// after a page load it is GET /api/users/me, which has `isCompanyAdmin`. Give every shape `isCompanyAdmin` so pages
// see the same admin flag whether or not the user reloaded.
const withAdminFlag = (user) => (user ? { ...user, isCompanyAdmin: !!(user.isCompanyAdmin ?? user.is_company_admin) } : user);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if there's a token in localStorage and verify it
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Check if token is expired
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decodedToken.exp < currentTime) {
          // Token is expired
          console.log('Token expired, logging out');
          logout();
        } else {
          // Token is valid, get user profile
          fetchUserProfile(token);
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        logout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Another tab signed out or signed someone else in (the token changed under this tab). Reload, so this tab drops the
  // previous user's chat held in memory instead of showing it or writing it back to storage (DT-1521).
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'token' && event.newValue !== event.oldValue) window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const fetchUserPermissions = async (token) => {
    try {
      const response = await api.get('/api/users/permissions', {
        headers: { 'x-auth-token': token }
      });

      const userPerms = response.data.permissions || [];

      // Render the server-resolved permissions verbatim.
      // Empty array = no access (matches hasModuleAccess semantics flipped 2026-01-08).
      setPermissions(userPerms);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      // Deny access on error — user can retry by refreshing
      setPermissions([]);
    }
  };

  const fetchUserProfile = async (token) => {
    try {
      const response = await api.get('/api/users/me', {
        headers: { 'x-auth-token': token }
      });
      claimUserScopedStorage(response.data?.id); // before setUser: no page may render another user's stored chat
      setUser(withAdminFlag(response.data));

      // Fetch permissions after successfully loading user profile
      await fetchUserPermissions(token);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      logout();
    }
  };

  // Sign-in responses carry only the token payload (no fullName, inventorySource, rawDataAccess, amazonSearchAccess,
  // createdAt). Load the same /api/users/me profile a page load gets; keep the payload if that call fails.
  const setSignedInUser = async (token, payloadUser) => {
    try {
      const response = await api.get('/api/users/me', {
        headers: { 'x-auth-token': token }
      });
      claimUserScopedStorage(response.data?.id); // before setUser: no page may render another user's stored chat
      setUser(withAdminFlag(response.data));
    } catch (error) {
      console.error('Error fetching user profile after sign-in:', error);
      claimUserScopedStorage(payloadUser?.id);
      setUser(withAdminFlag(payloadUser));
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const response = await api.post('/api/users/login', { email, password });
      if (response.data.mfa_required) {
        return { success: false, mfaRequired: true, mfaToken: response.data.mfa_token };
      }
      const { token, user: userData } = response.data;

      // Store token in localStorage
      localStorage.setItem('token', token);

      // Set user state
      await setSignedInUser(token, userData);

      // Fetch user permissions after successful login
      await fetchUserPermissions(token);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      setError(error.response?.data?.message || 'Failed to login. Please check your credentials.');
      return { success: false, error: error.response?.data?.message || 'Authentication failed' };
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async (credential) => {
    try {
      setError(null);
      setLoading(true);
      const response = await api.post('/api/users/google-login', { credential });
      if (response.data.mfa_required) {
        return { success: false, mfaRequired: true, mfaToken: response.data.mfa_token };
      }
      const { token, user: userData } = response.data;

      localStorage.setItem('token', token);
      await setSignedInUser(token, userData);
      await fetchUserPermissions(token);

      return { success: true };
    } catch (error) {
      console.error('Google login error:', error.response?.data || error.message);
      const errorCode = error.response?.data?.error_code;
      const message = error.response?.data?.message || 'Google sign-in failed.';
      setError(message);
      return { success: false, error: message, errorCode };
    } finally {
      setLoading(false);
    }
  };

  const microsoftLogin = async (idToken) => {
    try {
      setError(null);
      setLoading(true);
      const response = await api.post('/api/users/microsoft-login', { idToken });
      if (response.data.mfa_required) {
        return { success: false, mfaRequired: true, mfaToken: response.data.mfa_token };
      }
      const { token, user: userData } = response.data;

      localStorage.setItem('token', token);
      await setSignedInUser(token, userData);
      await fetchUserPermissions(token);

      return { success: true };
    } catch (error) {
      console.error('Microsoft login error:', error.response?.data || error.message);
      const errorCode = error.response?.data?.error_code;
      const message = error.response?.data?.message || 'Microsoft sign-in failed.';
      setError(message);
      return { success: false, error: message, errorCode };
    } finally {
      setLoading(false);
    }
  };

  // Second step of sign-in for users with 2-step verification on.
  const verifyMfa = async (mfaToken, code) => {
    try {
      setError(null);
      setLoading(true);
      const response = await api.post('/api/users/login/mfa', { mfa_token: mfaToken, code });
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      await setSignedInUser(token, userData);
      await fetchUserPermissions(token);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Verification failed.';
      return { success: false, error: message, code: error.response?.data?.code };
    } finally {
      setLoading(false);
    }
  };

  // Turning 2-step verification on returns a new session token; switch to it straight away,
  // because the old one is no longer accepted for this user.
  const adoptSessionToken = (token) => {
    if (token) localStorage.setItem('token', token);
  };

  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');
    // and this user's chat and selections, so the next person on this browser does not get them (DT-1521)
    clearSessionStorageKeys();
    // Clear user state and permissions
    setUser(null);
    setPermissions([]);
    setLoading(false);
  };

  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      await api.post('/api/users/register', userData);
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error.response?.data || error.message);
      setError(error.response?.data?.message || 'Failed to register. Please try again.');
      return { success: false, error: error.response?.data?.message || 'Registration failed' };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (userId, userData) => {
    try {
      setError(null);
      const response = await api.put(`/api/users/${userId}`, userData);

      // Update user state with new data from response
      // Backend returns { success, message, user } so we need response.data.user
      if (response.data && response.data.user) {
        setUser(prevUser => withAdminFlag({
          ...prevUser,
          ...response.data.user
        }));
      }

      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error.response?.data || error.message);
      setError(error.response?.data?.message || 'Failed to update profile.');
      return { success: false, error: error.response?.data?.message || 'Update failed' };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      permissions,
      loading,
      error,
      login,
      googleLogin,
      microsoftLogin,
      verifyMfa,
      adoptSessionToken,
      logout,
      register,
      updateProfile,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;